import React, {
  useContext, useEffect, useRef, useState
} from 'react';
import { useMutation } from '@apollo/client';
import useAlert, { TYPES } from '@hooks/useAlert/useAlert';
import ResultsResponsive, { simpleItemVisitor } from '@components/shared/ResultsResponsive/ResultsResponsive';
import AddToBoardModalContext from '@context/Modal/add-to-board-modal-context';
import boardItemTypes from '@constants/boardItemTypes';
import Loader from '@components/shared/Loader/Loader';
import UserContext from '@context/User/user-context';
import { isSavedBoard } from '@lib/board/isSavedBoard';
import { IDesignerProfile } from '@components/pages/DesignerProfile/graphql/designer-profile-query.graphql';
import { IDesignerBoard } from '@components/pages/DesignerProfile/graphql/designer-boards.graphql';
import { ResponsiveRowPattern } from '@components/shared/ResultsResponsive/ResponsiveTemplate';
import {
  ResponsiveTemplateDesktop1, ResponsiveTemplateDesktop2,
  ResponsiveTemplateDesktop3
} from '@components/shared/ResultsResponsive/ResponsiveTemplates/Desktop';
import FeaturedBoardModal from '@components/pages/DesignerProfile/DesignerProfileAdmin/FeaturedBoardModal/FeaturedBoardModal';
import {
  hideDesignerProfileBoard,
  unhideDesignerProfileBoard
} from '@components/pages/DesignerProfile/graphql/designer-board-visibility.graphql';
import useDesignerBoards from './hooks/useDesignerBoards';
import styles from './DesignerDesigns.module.scss';
import DesignerBoardCard from './DesignerBoardCard/DesignerBoardCard';

interface IDesignerDesignsProps {
  className?: string;
  designer: IDesignerProfile;
  editing?: boolean;
  initialBoards?: IDesignerBoard[];
}

interface IFeaturedBoardModalState {
  currentBoard: IDesignerBoard;
  newBoard: IDesignerBoard;
}

const DesignerDesigns = ({
  className = '',
  designer,
  editing = false,
  initialBoards = [],
}: IDesignerDesignsProps) => {
  const isInitialMount = useRef(true);

  const { user, savedAssetIds } = useContext(UserContext);
  const { open: openAddToBoard } = useContext(AddToBoardModalContext);

  const [featuredBoardModalState, setFeaturedBoardModalState] =
    useState<IFeaturedBoardModalState|null>(null);

  const [
    mutationHideDesignerProfileBoard,
  ] = useMutation(hideDesignerProfileBoard);

  const [
    mutationUnhideDesignerProfileBoard,
  ] = useMutation(unhideDesignerProfileBoard);

  const {
    boards,
    error,
    loading,
    loadingMore,
    reset,
    resetting,
  } = useDesignerBoards(designer.id, editing, initialBoards);

  const alert = useAlert(TYPES.SUCCESS);
  const errorAlert = useAlert(TYPES.ERROR);

  const featuredBoard = (boards || []).find((board:IDesignerBoard) => !board.isHidden);

  useEffect(() => {
    if (error) {
      errorAlert("Failed to load the designer's boards.", 3000);
    }
  }, [error]);

  useEffect(() => {
    // This useEffect only cares about updates, not initial mount.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    reset();
  }, [editing]);

  const handleOnSave = (typename: string, board: IDesignerBoard) => { // todo: better type for board
    if (typename === 'BoardModel') {
      const mainBoardAsset = board.mainBoardAsset?.asset;
      if (mainBoardAsset?.id && (!user?.role || user.role === 'customer')) {
        openAddToBoard({
          source: 'designer_profile',
          saveItemId: Number(mainBoardAsset.id),
          sourceBoard: {
            id: Number(board.id),
            designer: {
              id: Number(designer.id)
            },
            subStyles: board.subStyles
          },
          saveItemImageUrl: mainBoardAsset.url,
          saveItemType: boardItemTypes.ASSETS
        });
      }
    }
    return undefined;
  };

  const handleSetHidden = async (item: IDesignerBoard, isHidden: boolean) => {
    try {
      if (isHidden) {
        await mutationHideDesignerProfileBoard({
          variables: {
            boardId: item.id,
          },
        });
      } else {
        await mutationUnhideDesignerProfileBoard({
          variables: {
            boardId: item.id,
          },
        });
      }
    } catch (err) {
      errorAlert(`Failed to ${isHidden ? 'hide' : 'unhide'} your board.`, 3000);
    }

    alert(`Your board has been ${isHidden ? 'hidden' : 'unhidden'}`, 3000);
  };

  return (
    <>
      <div className={className}>
        {
          (boards && !resetting) && (
            <div className={ styles.ResultsContainer }>

              <ResultsResponsive
                desktopPattern={new ResponsiveRowPattern([
                  ResponsiveTemplateDesktop3,
                  ResponsiveTemplateDesktop1,
                  ResponsiveTemplateDesktop2
                ])}
                itemVisitorProvider={() => simpleItemVisitor(
                  boards || [],
                  (item, size) => (
                    <DesignerBoardCard
                      board={item}
                      designer={designer}
                      saved={isSavedBoard(item, savedAssetIds)}
                      size={size}
                      editing={ editing }
                      featured={ item.id === featuredBoard?.id }
                      handleSetFeatured={
                        (board: IDesignerBoard) => {
                          setFeaturedBoardModalState({
                            currentBoard: featuredBoard!,
                            newBoard: board,
                          });
                        }
                      }
                      handleSetHidden={ handleSetHidden }
                      key={ item.id }
                      onSave={ handleOnSave }
                    />
                  )
                )}
                displayPartialRows
              />
            </div>
          )
        }

        <div className={ styles.LoadingWrap }>
          { (loading || loadingMore || resetting) && (<Loader />)}
        </div>
      </div>

      {featuredBoardModalState && (
        <FeaturedBoardModal
          currentFeaturedBoard={featuredBoardModalState.currentBoard}
          newFeaturedBoard={featuredBoardModalState.newBoard}
          isVisible
          close={() => setFeaturedBoardModalState(null)}
          onSuccess={() => reset()}
        />
      )}
    </>
  );
};

export default DesignerDesigns;

